/**
 * Operating-room etiquette shared by the OR levels: scrubbing, gowning,
 * contamination, the time-out checklist and the patient on the table.
 */
import {
  annoy,
  C,
  type Choice,
  type ChoiceItem,
  closeZoom,
  type Ctx,
  type Dialog,
  eventBanner,
  me,
  post,
  session,
  spend,
  T,
  tr,
} from "../../engine";

/** Session fields a level needs to use these helpers. */
export interface SterileVars {
  scrubbed: boolean;
  sterile: boolean;
  /** Sent off the table by the scrub nurse after three contaminations. */
  banned: boolean;
  contam: number;
  atCamera: boolean;
}

const S = session<SterileVars>();

/** Scrub nurse cast key and her log channel. */
const NURSE = "scrub";
const NCH = "scrubn";

/** Break sterility (no-op when not gowned). Three strikes and the scrub nurse sends you off. */
export function contaminate(why: string): void {
  if (!S.sterile) return;
  S.sterile = false;
  S.atCamera = false;
  S.contam++;
  closeZoom();
  const n = annoy(
    NURSE,
    tr(
      [
        `${why}你污染了！去換手術衣、重新戴手套！`,
        `${why}又污染？今天第二次了。換掉！`,
        `${why}第三次。你出去，今天不用上了。`,
      ],
      [
        `${why} You’re contaminated! Go change your gown and reglove!`,
        `${why} Contaminated again? That’s twice today. Change!`,
        `${why} That’s three. Out. You’re done for today.`,
      ],
    ),
    NCH,
  );
  if (n >= 3) {
    S.banned = true;
    eventBanner(tr("被請下台", "Sent off the table"), tr("阿珍：今天不用上了", "Jen: You’re done for today"), true);
  }
}

/** Call from `Level.beforeInteract`: touching a non-sterile thing once scrubbed or gowned. */
export function guardTouch(id: string, nonsterile: readonly string[]): void {
  if (S.sterile && nonsterile.includes(id))
    contaminate(tr("你碰到不是無菌的東西了。", "You touched something non-sterile."));
  else if (S.scrubbed && !S.sterile && nonsterile.includes(id) && id !== "scrub") {
    S.scrubbed = false;
    annoy(
      NURSE,
      tr(
        ["刷完手亂摸什麼！回去重刷！", "你又摸！重刷！", "……我放棄了。重刷。"],
        [
          "You just scrubbed, stop touching things! Go scrub again!",
          "You touched it again! Rescrub!",
          "…I give up. Rescrub.",
        ],
      ),
      NCH,
    );
  }
}

/** The scrub sink. */
export function scrubSinkDialog(): Dialog {
  return {
    title: tr("刷手台", "Scrub sink"),
    sub: tr("刷手區", "Scrub area"),
    ch: "scrub",
    choices() {
      return [
        C(
          tr("刷手（3 分鐘）", "Scrub (3 min)"),
          () => {
            me("scrub", tr("（刷手）", "(Scrubs in)"));
            S.scrubbed = true;
            post(
              "scrub",
              "narr",
              null,
              tr(
                "你從指尖刷到手肘上方，手舉高讓水往手肘流。刷好了，手不要再碰任何東西。",
                "You scrub from fingertips to above the elbows, hands held high so the water runs off at the elbows. Done. Don’t touch anything with those hands now.",
              ),
            );
            spend(3);
          },
          S.scrubbed || S.sterile,
        ),
      ];
    },
  };
}

/** "Help me gown and glove" for the scrub nurse's dialog. `late()` = the case is already closing. */
export function gownChoice(late: () => boolean): Choice {
  return C(tr("請她幫你穿手術衣、戴手套", "Ask her to gown and glove you"), () => {
    me(NCH, tr("（請阿珍幫你穿手術衣）", "(Asks Jen to gown you)"));
    if (S.sterile) {
      post(NCH, "npc", NURSE, tr("你已經穿好了。手放胸前。", "You’re already gowned. Hands on your chest."));
      return;
    }
    if (S.banned) {
      post(NCH, "npc", NURSE, tr("你今天不用上了。", "You’re done for today."));
      return;
    }
    if (late()) {
      annoy(
        NURSE,
        tr(
          ["都在關了你現在才要穿？省省吧。", "都說了不用了。"],
          ["We’re closing and you want to gown now? Don’t bother.", "I already said no."],
        ),
        NCH,
        "npc",
      );
      return;
    }
    if (!S.scrubbed) {
      post(NCH, "npc", NURSE, tr("你刷手了嗎？沒刷手不能穿。", "Did you scrub? No scrub, no gown."));
      return;
    }
    S.sterile = true;
    post(
      NCH,
      "npc",
      NURSE,
      S.contam
        ? tr(
            "這次給我小心一點。手放胸前，不要碰任何東西。",
            "Be careful this time. Hands on your chest, don’t touch anything.",
          )
        : tr(
            "手伸進來……好，手套。手放胸前，不要碰任何東西。",
            "Arms in… good, gloves. Hands on your chest, don’t touch anything.",
          ),
    );
    spend(2);
  });
}

export const sterileStatus = (): string =>
  S.banned
    ? tr("被請下台", "Sent off the table")
    : S.sterile
      ? tr("無菌（手術衣＋手套）", "Sterile (gown + gloves)")
      : S.scrubbed
        ? tr("刷過手，還沒穿手術衣", "Scrubbed, not gowned yet")
        : tr("還沒刷手", "Not scrubbed yet");

/* ---------- time-out ---------- */

export interface TimeoutOpt {
  v: string;
  ok: boolean;
  reply: string;
}

export interface TimeoutItem {
  k: string;
  opts: TimeoutOpt[];
}

export interface TimeoutCfg {
  ch: string;
  /** Who confirms a right answer / corrects a wrong one. */
  okWho: string;
  badWho: string;
  onWrong?(): void;
  /** All items answered. */
  onDone(): void;
}

/** Answer buttons for the time-out checklist; `ans` records item → chosen answer. */
export function timeoutChoices(items: TimeoutItem[], ans: Record<string, string>, o: TimeoutCfg): ChoiceItem[] {
  const out: ChoiceItem[] = [];
  for (const it of items) {
    out.push(it.k);
    for (const op of it.opts)
      out.push(
        C(
          op.v,
          () => {
            ans[it.k] = op.v;
            me(o.ch, it.k + tr("：", ": ") + op.v);
            post(o.ch, "npc", op.ok ? o.okWho : o.badWho, op.reply);
            if (!op.ok) o.onWrong?.();
            if (Object.keys(ans).length >= items.length) o.onDone();
          },
          !!ans[it.k],
        ),
      );
  }
  return out;
}

/* ---------- the patient on the table ---------- */

export interface TableOpts {
  /** Tile of the table's head end. */
  x: number;
  y: number;
  /** Extra tiles of travel (wheeled out). */
  oy?: number;
  draped: boolean;
  /** Show the anaesthesia mask. */
  asleep: boolean;
  hair: string;
  skin: string;
  gown?: string;
  /** Drape window colour (incision site). */
  site?: string;
  /** Vertical offset (px) of the drape window, e.g. upper abdomen vs pelvis. */
  siteY?: number;
}

/** Operating table with the patient: gown when awake, blue drapes once prepped. 3 tiles long. */
export function drawOrTable(c: Ctx, o: TableOpts): void {
  const px = o.x * T;
  const py = Math.round((o.y + (o.oy ?? 0)) * T);
  c.fillStyle = "#5a6070";
  c.fillRect(px + 1, py + 2, 14, 44);
  c.fillStyle = "#8790a8";
  c.fillRect(px + 2, py + 1, 12, 46);
  if (o.draped) {
    c.fillStyle = "#2f5f9e";
    c.fillRect(px, py + 6, 16, 40);
    c.fillStyle = "#3f72b5";
    c.fillRect(px + 2, py + 16, 12, 14);
    c.fillStyle = o.site ?? "#c0394a";
    c.fillRect(px + 6, py + (o.siteY ?? 22), 4, 3);
  } else {
    c.fillStyle = o.gown ?? "#a9c8e8";
    c.fillRect(px + 2, py + 12, 12, 32);
  }
  c.fillStyle = o.hair;
  c.fillRect(px + 5, py + 2, 6, 5);
  c.fillStyle = o.skin;
  c.fillRect(px + 6, py + 4, 4, 3);
  if (o.asleep) {
    c.fillStyle = "#e6f2f8";
    c.fillRect(px + 6, py + 5, 4, 2);
  }
}
